/*
 * Copyright (c) 2024. Tooling Team
 */

export const merge = (target, source, prop) => {
  source.forEach((sourceElement) => {
    const targetElement = target.find((targetElement) => {
      return sourceElement[prop] === targetElement[prop];
    });
    targetElement
      ? Object.assign(targetElement, sourceElement)
      : target.push(sourceElement);
  });
};

// Resolves a stable row identifier from persisted records and newly added rows.
export function getRecordRowId(record) {
  if (!record) {
    return undefined;
  }

  if (record._id !== null && record._id !== undefined) {
    return String(record._id);
  }

  if (record.addRowId !== null && record.addRowId !== undefined) {
    return String(record.addRowId);
  }

  return undefined;
}

// API expects sourceConcept and targetConcept
export function transformMVRecordsForApiRequest(updatedRecords) {
  const transformedRecords = [];
  for (var record of updatedRecords) {
    const transformedRecord = Object.assign({}, record);
    delete transformedRecord.localSourceSystem;
    delete transformedRecord.localCode;
    delete transformedRecord.localDescription;
    delete transformedRecord.standardCode;
    delete transformedRecord.standardDescription;
    delete transformedRecord.standardLongDescription;
    delete transformedRecord.standardCodeSystem;

    transformedRecord.sourceConcept = {};
    if (record.localSourceSystem) {
      transformedRecord.sourceConcept.localSourceSystem =
        record.localSourceSystem;
    }
    if (record.localCode) {
      transformedRecord.sourceConcept.localCode = record.localCode;
    }
    if (record.localDescription) {
      transformedRecord.sourceConcept.localDescription =
        record.localDescription;
    }

    transformedRecord.targetConcept = {};
    if (record.standardCode) {
      transformedRecord.targetConcept.standardCode = record.standardCode;
    }
    if (record.standardDescription) {
      transformedRecord.targetConcept.standardDescription =
        record.standardDescription;
    }
    if (record.standardLongDescription) {
      transformedRecord.targetConcept.standardLongDescription =
        record.standardLongDescription;
    }
    if (record.standardCodeSystem) {
      transformedRecord.targetConcept.standardCodeSystem =
        record.standardCodeSystem;
    }
    transformedRecords.push(transformedRecord);
  }

  return transformedRecords;
}

const UNCHANGED = "UNCHANGED";

// Writes a value into a nested object path while creating missing parent objects.
const setNestedValue = (target, path, value) => {
  let current = target;
  path.forEach((key, index) => {
    if (index === path.length - 1) {
      current[key] = value;
      return;
    }
    if (!current[key]) {
      current[key] = {};
    }
    current = current[key];
  });
};

// Converts bulk-edit form fields into the nested API shape expected by the backend.
export function transformBulkEditUpdatesForApiRequest(updates) {
  const transformedUpdates = {};
  const fieldMap = {
    status: ["status"],
    comments: ["comments"],
    localSourceSystem: ["sourceConcept", "localSourceSystem"],
    localDescription: ["sourceConcept", "localDescription"],
    standardCode: ["targetConcept", "standardCode"],
    standardDescription: ["targetConcept", "standardDescription"],
    standardLongDescription: ["targetConcept", "standardLongDescription"],
  };

  Object.entries(fieldMap).forEach(([field, path]) => {
    if (
      Object.hasOwn(updates, field) &&
      updates[field] !== UNCHANGED &&
      updates[field] !== undefined
    ) {
      setNestedValue(transformedUpdates, path, updates[field]);
    }
  });

  return transformedUpdates;
}

// Builds the bulk-edit request body when the selected rows and changes qualify for the bulk API.
export function buildBulkEditPayload(rows, updates, options = {}) {
  if (
    !Array.isArray(rows) ||
    rows.length <= 2 ||
    rows.some((row) => !row || !row._id) ||
    (Object.hasOwn(updates, "localCode") && updates.localCode !== UNCHANGED)
  ) {
    return null;
  }

  const transformedUpdates = transformBulkEditUpdatesForApiRequest(updates);
  if (!Object.keys(transformedUpdates).length) {
    return null;
  }

  const selection = options.selection || {};
  if (selection.useAllMapTarget && selection.mapId) {
    return {
      target: {
        type: "ALL",
        mapId: selection.mapId,
      },
      updates: transformedUpdates,
    };
  }

  return {
    ids: rows.map((row) => row._id),
    updates: transformedUpdates,
  };
}

// API sends sourceConcept and targetConcept properties, this function pulls their children up as record properties
export function transformMVApiResponse(records) {
  for (var record of records) {
    if (record.sourceConcept) {
      if (record.sourceConcept.localSourceSystem) {
        record.localSourceSystem = record.sourceConcept.localSourceSystem;
      }
      if (record.sourceConcept.localCode) {
        record.localCode = record.sourceConcept.localCode;
      }
      if (record.sourceConcept.localDescription) {
        record.localDescription = record.sourceConcept.localDescription;
      }
      if (record.sourceConcept.localLongDescription) {
        record.localLongDescription = record.sourceConcept.localLongDescription;
      } else if (record.sourceConcept.longDescription) {
        record.localLongDescription = record.sourceConcept.longDescription;
      }
      delete record.sourceConcept;
    }

    if (record.targetConcept) {
      if (record.targetConcept.standardCode) {
        record.standardCode = record.targetConcept.standardCode;
      }
      if (record.targetConcept.standardDescription) {
        record.standardDescription = record.targetConcept.standardDescription;
      }
      if (record.targetConcept.standardLongDescription) {
        record.standardLongDescription =
          record.targetConcept.standardLongDescription;
      }
      if (record.targetConcept.standardCodeSystem) {
        record.standardCodeSystem = record.targetConcept.standardCodeSystem;
      }

      delete record.targetConcept;
    }
  }
}

// Grid columns are rendered under a "Local" or "Standard" section header when
// the API's primaryName is prefixed that way (e.g. "Local Source System" ->
// section "Local", label "System"). Splitting it here means the grid only
// needs to check for column.section, instead of hardcoding which keys belong
// to which section.
const COLUMN_SECTION_PREFIXES = ["Local", "Standard"];

// TODO: backend defect - mapElements.columns omits lastEditedAt/lastEditedBy/
// tags even though records carry those fields; append them manually so the
// grid still renders them. Remove once the backend includes them in the
// response.
// TODO: once backend issue "mapElements.columns should include/omit tags
// based on whether records have tag data" is fixed, remove the tags entry
// here, the hasTagData filtering/repositioning below, and the records
// parameter on this function entirely.
const MANUALLY_ADDED_COLUMNS = [
  { key: "lastEditedAt", primaryName: "Last Edited At" },
  { key: "lastEditedBy", primaryName: "Last Edited By" },
  { key: "tags", primaryName: "Tags" },
];

export function deriveMapColumnSections(columns, records = []) {
  if (!Array.isArray(columns)) {
    return columns;
  }

  const derivedColumns = columns.map((column) => {
    const tokens = (column.primaryName || "").split(" ");
    const [firstToken] = tokens;

    if (tokens.length > 1 && COLUMN_SECTION_PREFIXES.includes(firstToken)) {
      return {
        ...column,
        section: firstToken,
        primaryName: tokens[tokens.length - 1],
      };
    }

    return column;
  });

  const existingKeys = new Set(derivedColumns.map((column) => column.key));
  const missingColumns = MANUALLY_ADDED_COLUMNS.filter(
    (column) => !existingKeys.has(column.key)
  );

  // Tags is only a real column when the map data actually has tags; drop it
  // here (whether it came from the backend or from MANUALLY_ADDED_COLUMNS
  // above) instead of leaving that decision to the grid component, so this
  // still works once the backend fixes the missing-tags-column defect.
  const hasTagData = records.some(
    (record) => record.tags && record.tags.length > 0
  );
  const allColumns = [...derivedColumns, ...missingColumns].filter(
    (column) => column.key !== "tags" || hasTagData
  );

  if (!hasTagData) {
    return allColumns;
  }

  // Tags has no Local/Standard section of its own; the grid expects it
  // rendered right after the Local columns, so position it here rather than
  // in the grid component.
  const tagsColumn = allColumns.find((column) => column.key === "tags");
  const otherColumns = allColumns.filter((column) => column.key !== "tags");
  const lastLocalIndex = otherColumns.reduce(
    (lastIndex, column, index) =>
      column.section === "Local" ? index : lastIndex,
    -1
  );
  const insertIndex = lastLocalIndex + 1;

  otherColumns.splice(insertIndex, 0, tagsColumn);
  return otherColumns;
}

export function toTitleCase(str) {
  return str.replace(/\w\S*/g, function (txt) {
    return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
  });
}

//getFilteredRowData:
//given row data and filter parameters, return filtered rows
//parameters:   allRowData - array of all row data for a client
//              data/synSoureValue - string value of desired filter sources, can be blank
//return value: filteredRowData - array of rows for ds/ss parameters
export const getFilteredRowData = (
  allRowData,
  dataSourceValue,
  synSourceValue
) => {
  const withTagName = (name) => {
    return (tag) => tag.name === name;
  };
  const withTagValue = (value) => {
    return (tagValue) => tagValue === value;
  };

  var filteredRows = [];
  const dataSourceName = "Data Source";
  const synSourceName = "Synonym Source";

  //get data for only the ds and ss input
  //else if only either ds or ss is selected, filter for that only
  if (dataSourceValue && synSourceValue) {
    filteredRows = allRowData.filter((row) => {
      if (
        row.tags &&
        row.tags.some(withTagName(dataSourceName)) &&
        row.tags.some(withTagName(synSourceName))
      ) {
        return (
          row.tags
            .find(withTagName(dataSourceName))
            .values.some(withTagValue(dataSourceValue)) &&
          row.tags
            .find(withTagName(synSourceName))
            .values.some(withTagValue(synSourceValue))
        );
      }
    });
  } else if (dataSourceValue && !synSourceValue) {
    filteredRows = allRowData.filter((row) => {
      if (
        row.tags &&
        row.tags.some(withTagName(dataSourceName)) &&
        !row.tags.some(withTagName(synSourceName))
      ) {
        return row.tags
          .find(withTagName(dataSourceName))
          .values.some(withTagValue(dataSourceValue));
      }
    });
  } else if (synSourceValue && !dataSourceValue) {
    filteredRows = allRowData.filter((row) => {
      if (
        row.tags &&
        !row.tags.some(withTagName(dataSourceName)) &&
        row.tags.some(withTagName(synSourceName))
      ) {
        return row.tags
          .find(withTagName(synSourceName))
          .values.some(withTagValue(synSourceValue));
      }
    });
  }
  return filteredRows;
};

//validation for add/edit and clone dialogs
export const isLocalCodeInTable = (records, localSystem, localCode) => {
  if (records === undefined) {
    return false;
  }

  let combinedInputCode = "";
  if (localSystem) {
    combinedInputCode += localSystem.trim();
  }
  combinedInputCode += localCode.trim();
  // localSystem + local code (case sensitive) cannot already exist in the table
  //todo: get a set of localSystem/localcode instead of looping through each existing record- will get expensive with large tables
  for (const record of records) {
    //Adding a check to skip uniqueness check on records which are deleted either in working copy or draft state
    if (
      !(record.flags && record.flags.deleted === true) ||
      record.delete === true
    ) {
      let recordCombinedCode = "";
      if (record.localSourceSystem) {
        recordCombinedCode += record.localSourceSystem.trim();
      }
      recordCombinedCode += record.localCode.trim();
      console.log(
        "combinedInputCode: " + combinedInputCode + " " + recordCombinedCode
      );
      console.log(combinedInputCode === recordCombinedCode);
      if (combinedInputCode === recordCombinedCode) {
        return true;
      }
    }
  }
  return false;
};

// Get dropdown object from value- needed to fetch object in Abyss select events as the event is only the value, not the object
export const getSelectedObject = (value, array) => {
  return value ? array.filter((object) => object.value === value)[0] : null;
};

/**
 * Adds a uniqueId property to each object in the array, starting from 1.
 * @param {Array} arr - The array of objects to augment.
 * @returns {Array} New array with uniqueId added to each object.
 */
export function addUniqueId(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((obj, idx) => ({
    ...obj,
    uniqueId: idx + 1,
  }));
}
